import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertOctagon, AlertTriangle, Info, ShieldCheck } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { TechniquesResult } from '../../../types/analysis-session';
import type { TechniqueDefinition } from '../../../services/technique-definitions.service';
import { HeadlineCard } from './HeadlineCard';
import { FindingCard } from './FindingCard';
import { renderTechniquesEditorial } from '../details/techniques';
import { StatBlock, ChipsRow, Chip } from '../styles';

const scoreAccent = (s: number): string =>
  s >= 70 ? '#ef4444' : s >= 40 ? '#f97316' : s >= 20 ? '#eab308' : '#22c55e';

const severityKey = (s: number): string => {
  if (s >= 70) return 'analysis.techniques.severity.critical';
  if (s >= 40) return 'analysis.techniques.severity.high';
  if (s >= 20) return 'analysis.techniques.severity.medium';
  return 'analysis.techniques.severity.low';
};

const scoreIcon = (s: number): LucideIcon => {
  if (s >= 70) return AlertOctagon;
  if (s >= 40) return AlertTriangle;
  if (s >= 20) return Info;
  return ShieldCheck;
};

interface Props {
  result: TechniquesResult;
  techDefs: TechniqueDefinition[];
}

/** Editorial result section for Manipulation Techniques — shared between live standalone and history detail. */
export const TechniquesResults: React.FC<Props> = ({ result, techDefs }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const score = result.manipulation_score ?? 0;
  const accent = scoreAccent(score);
  const Icon = scoreIcon(score);
  const techCount = result.techniques_detected.length;
  const dimCount = result.dimensions_affected.length;
  const dims = result.dimensions_affected.join(' · ');
  const warningFlags = result.coupling_context?.for_claims?.warning_flags ?? [];

  const buildTldr = (): string => {
    if (techCount === 0) return t('analysis.techniques.noTechniquesLong');
    return t('analysis.techniques.tldrSummary', {
      techCount,
      techNoun: t('analysis.techniques.technique', { count: techCount }),
      dimCount,
      dimNoun: t('analysis.techniques.dimension', { count: dimCount }),
      dimsSuffix: dims ? ` (${dims})` : '',
    });
  };

  return (
    <>
      <HeadlineCard
        accent={accent}
        score={score}
        scoreLabel={t('analysis.techniques.scoreSuffix')}
        eyebrow={t('analysis.techniques.title')}
        icon={Icon}
        category={t(severityKey(score))}
        descriptor={
          <>
            <span>{techCount} {t('analysis.techniques.technique', { count: techCount })}</span>
            {dimCount > 0 && (
              <>
                <span className="sep">·</span>
                <span>{dimCount} {t('analysis.techniques.dimension', { count: dimCount })}</span>
              </>
            )}
          </>
        }
        tldr={buildTldr()}
      />

      {(score > 0 || dimCount > 0) && (
        <StatBlock>
          <ChipsRow>
            {result.dimensions_affected.map(dim => (
              <Chip key={dim} variant="info">{dim}</Chip>
            ))}
            {techCount > 0 && (
              <Chip variant="violet">
                {techCount} {t('analysis.techniques.chipTechniquesDetected')}
              </Chip>
            )}
          </ChipsRow>
        </StatBlock>
      )}

      {techCount > 0 && renderTechniquesEditorial(
        result,
        techDefs,
        t,
        expanded,
        () => setExpanded(o => !o),
      )}

      {techCount === 0 && (
        <FindingCard
          icon={ShieldCheck}
          accent="success"
          eyebrow={t('analysis.techniques.cleanContent')}
          headline={t('analysis.techniques.noTechniquesShort')}
        />
      )}

      {warningFlags.length > 0 && (
        <FindingCard
          icon={AlertTriangle}
          accent="warning"
          eyebrow={t('analysis.techniques.additionalSignals')}
          headline={t('analysis.techniques.screeningSignals')}
          quote={warningFlags.map(f => f.replace(/_/g, ' ')).join(' · ')}
        />
      )}
    </>
  );
};
